import {Component, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import {MultiSelectComponent, MultiSelectFilter} from '../multi-select-component';
import {VehicleCategoryModel} from '../../models/vehicle';
import {MatMenu, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {VehicleGroupFilterComponent} from '../vehicle-group-filter/vehicle-group-filter.component';
import {DateFilter, LabeledDateFilter, TimeRangeFilter} from '../../models/DateFilter';
import {DateFilterDropdownComponent} from '../date-filter-dropdown/date-filter-dropdown.component';
import {ObservationTypeGroup} from '../../models/observation-group';
import {AssetStatus} from '../../models/asset-status';
import {ObservationGroupFilterComponent} from '../observation-group-filter/observation-group-filter.component';
import {AssetStatusFilterComponent} from '../asset-status-filter/asset-status-filter.component';
import {MatChipListbox, MatChipOption} from '@angular/material/chips';
import {NgClass} from '@angular/common';
import {MatIcon} from '@angular/material/icon';
import { VehicleFilterComponent } from '../vehicle-filter/vehicle-filter.component';
import { VehicleModel } from '../../models/vehicle.model';
import { DriverFilterComponent } from '../driver-filter/driver-filter.component';
import { DriverWithShiftCount } from '../../models/driver.model';
import { UserEmailAsId, UserFilterComponent } from '../user-filter/user-filter.component';
import {
  RouteAssignmentStatusItem,
  RouteAssignmentStatusFilterComponent
} from '../route-assignment-status-filter/route-assignment-status-filter.component';
import { PassFailFilterComponent } from '../pass-fail-filter/pass-fail-filter.component';
import { NamedId } from '../../models/NamedId';

interface Chip {
  type: ChipType;
  filterName: string;
  clear: boolean;
  items: string;
}

enum ChipType {
  DATE_FILTER,
  VEHICLE_GROUP_FILTER,
  VEHICLE_FILTER,
  DRIVER_FILTER,
  USER_FILTER,
  OBSERVATION_TYPE_GROUP_FILTER,
  ASSET_STATUS_FILTER,
  ROUTE_ASSIGNMENT_STATUS_FILTER,
  PASS_FAIL_FILTER,
}

@Component({
  selector: 'app-filter-bar',
  templateUrl: './filter-bar.component.html',
  styleUrls: ['./filter-bar.component.scss'],
  imports: [
    MatChipListbox,
    MatChipOption,
    MatIcon,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    NgClass,
    AssetStatusFilterComponent,
    ObservationGroupFilterComponent,
    DateFilterDropdownComponent,
    VehicleGroupFilterComponent,
    VehicleFilterComponent,
    DriverFilterComponent,
    UserFilterComponent,
    RouteAssignmentStatusFilterComponent,
    PassFailFilterComponent,
  ],
})
export class FilterBarComponent {
  @Input() origin: string;
  @Input() useDateFilter = false;
  @Input() defaultDateFilter: TimeRangeFilter;
  @Input() useVehicleGroupFilter = false;
  @Input() useVehicleFilter = false;
  @Input() useDriverFilter = false;
  @Input() useUserFilter = false;
  @Input() useObservationTypeGroupFilter = false;
  @Input() useAssetStatusFilter = false;
  @Input() useRouteAssignmentStatusFilter = false;
  @Input() usePassFailFilter = false;

  @Output() dateFilterChanged = new EventEmitter<DateFilter>();
  @Output() vehicleGroupFilterChanged = new EventEmitter<MultiSelectFilter<number>>();
  @Output() vehicleFilterChanged = new EventEmitter<MultiSelectFilter<number>>();
  @Output() driverFilterChanged = new EventEmitter<MultiSelectFilter<number>>();
  @Output() userFilterChanged = new EventEmitter<MultiSelectFilter<string>>();
  @Output() observationTypeGroupFilterChanged = new EventEmitter<MultiSelectFilter<number>>();
  @Output() assetStatusFilterChanged = new EventEmitter<MultiSelectFilter<string>>();
  @Output() routeAssignmentStatusFilterChanged = new EventEmitter<MultiSelectFilter<string>>();
  @Output() passFailFilterChanged = new EventEmitter<string>();
  @Output() appliedFiltersChanged = new EventEmitter<number>();

  dateFilter: LabeledDateFilter = undefined;
  vehicleGroupFilter: VehicleCategoryModel[] = undefined;
  vehicleFilter: VehicleModel[] = undefined;
  driverFilter: DriverWithShiftCount[] = undefined;
  userFilter: UserEmailAsId[] = undefined;
  observationTypeGroupFilter: ObservationTypeGroup[] = undefined;
  assetStatusFilter: AssetStatus[] = undefined;
  routeAssignmentStatusFilter: RouteAssignmentStatusItem[] = undefined;
  passFailFilter: NamedId = undefined;

  filterChips: Chip[] = [];
  @Input() showingFilterBar = false;

  @ViewChild('matTriggerAdd') matTriggerAdd: MatMenuTrigger;
  @ViewChild('matTriggerVehicleGroup') matTriggerVehicleGroup: MatMenuTrigger;
  @ViewChild(VehicleGroupFilterComponent) vgFilterComponent: VehicleGroupFilterComponent;

  @ViewChild('matTriggerVehicle') matTriggerVehicle: MatMenuTrigger;
  @ViewChild(VehicleFilterComponent) vehicleFilterComponent: VehicleFilterComponent;

  @ViewChild('matTriggerDriver') matTriggerDriver: MatMenuTrigger;
  @ViewChild(DriverFilterComponent) driverFilterComponent: DriverFilterComponent;

  @ViewChild('matTriggerUser') matTriggerUser: MatMenuTrigger;
  @ViewChild(UserFilterComponent) userFilterComponent: UserFilterComponent;

  @ViewChild('matTriggerDateFilter') matTriggerDateFilter: MatMenuTrigger;
  @ViewChild(DateFilterDropdownComponent) dateFilterComponent: DateFilterDropdownComponent;

  @ViewChild('matTriggerObservationTypeGroup') matTriggerObservationTypeGroup: MatMenuTrigger;
  @ViewChild(ObservationGroupFilterComponent) otgFilterComponent: ObservationGroupFilterComponent;

  @ViewChild('matTriggerAssetStatus') matTriggerAssetStatus: MatMenuTrigger;
  @ViewChild(AssetStatusFilterComponent) asFilterComponent: AssetStatusFilterComponent;

  @ViewChild('matTriggerRouteAssignmentStatus') matTriggerRouteAssignmentStatus: MatMenuTrigger;
  @ViewChild(RouteAssignmentStatusFilterComponent) routeAssignmentStatusFilterComponent: RouteAssignmentStatusFilterComponent;

  @ViewChild('matTriggerPassFail') matTriggerPassFail: MatMenuTrigger;
  @ViewChild(PassFailFilterComponent) passFailFilterComponent: PassFailFilterComponent;

  allFiltersSet(): boolean {
    return (
      (!this.useDateFilter || (!!this.dateFilter && (!!this.dateFilter.from || this.dateFilter.to))) &&
      (!this.useVehicleGroupFilter || !!this.vehicleGroupFilter) &&
      (!this.useVehicleFilter || !!this.vehicleFilter) &&
      (!this.useDriverFilter || !!this.driverFilter) &&
      (!this.useUserFilter || !!this.userFilter) &&
      (!this.useObservationTypeGroupFilter || !!this.observationTypeGroupFilter) &&
      (!this.useAssetStatusFilter || !!this.assetStatusFilter) &&
      (!this.useRouteAssignmentStatusFilter || !!this.routeAssignmentStatusFilter) &&
      (!this.usePassFailFilter || !!this.passFailFilter )
    );
  }

  noFiltersSet(): boolean {
    return (
      (!this.useDateFilter || (!!this.dateFilter && !this.dateFilter.from && !this.dateFilter.to)) &&
      (!this.useVehicleGroupFilter || !this.vehicleGroupFilter) &&
      (!this.useVehicleFilter || !this.vehicleFilter) &&
      (!this.useDriverFilter || !this.driverFilter) &&
      (!this.useUserFilter || !this.userFilter) &&
      (!this.useObservationTypeGroupFilter || !this.observationTypeGroupFilter) &&
      (!this.useAssetStatusFilter || !this.assetStatusFilter) &&
      (!this.useRouteAssignmentStatusFilter || !this.routeAssignmentStatusFilter) &&
      (!this.usePassFailFilter || !this.passFailFilter )
    );
  }

  clearFilters() {
    this.filterChips.forEach(chip => {
      this.filterChipRemoved(null, chip);
    });
    this.filterChips = [];
  }

  public overrideVehicleFilter(vehicles: VehicleModel[]) {
    this.vehicleFilterComponent.setFilter(vehicles);
  }

  onDateFilterChanged(dateFilter: LabeledDateFilter) {
    this.dateFilter = dateFilter;

    // update dateFilter chip
    if (this.useDateFilter) {
      const chipIndex = this.filterChips.findIndex(chip => chip.type === ChipType.DATE_FILTER);
      if (dateFilter) {
        const chip = {
          type: ChipType.DATE_FILTER,
          filterName: 'Date Range',
          clear: !dateFilter.from && !dateFilter.to,
          items: dateFilter.label,
        };
        if (chipIndex >= 0) {
          this.filterChips[chipIndex] = chip;
        } else {
          this.filterChips.push(chip);
        }
      } else if (chipIndex >= 0) {
        this.filterChips.splice(chipIndex, 1);
      }
    }

    // emit event to parent
    this.dateFilterChanged.emit(dateFilter);
    this.appliedFiltersChanged.emit(this.filterChips.filter(chip => !chip.clear).length);
  }

  onVehicleGroupsChanged(filter: MultiSelectFilter<VehicleCategoryModel>) {
    this.vehicleGroupFilter = filter.elements;

    // update vehicle group chip
    if (this.useVehicleGroupFilter) {
      const chipIndex = this.filterChips.findIndex(chip => chip.type === ChipType.VEHICLE_GROUP_FILTER);
      const filterName = 'Vehicle Group';
      let chip: Chip;
      let itemLabel: string;
      if (filter.elements?.length > 0) {
        const firstGroup = filter.elements[0];
        itemLabel = this.vgFilterComponent.getLabel(firstGroup);
        if (filter.elements.length > 1) {
          itemLabel = `${itemLabel}, +${filter.elements.length - 1}`;
        }
      } else {
        itemLabel = 'All';
      }
      chip = {
        type: ChipType.VEHICLE_GROUP_FILTER,
        filterName,
        clear: !filter.elements,
        items: itemLabel,
      };
      if (chipIndex >= 0) {
        this.filterChips[chipIndex] = chip;
      } else {
        this.filterChips.push(chip);
      }
    }

    // emit event to parent
    this.vehicleGroupFilterChanged.emit(new MultiSelectFilter<number>(filter.elements ? filter.elements.map(group => group.id) : null));
    this.appliedFiltersChanged.emit(this.filterChips.filter(chip => !chip.clear).length);
  }

  onVehicleChanged(filter: MultiSelectFilter<VehicleModel>) {
    this.vehicleFilter = filter.elements;

    // update vehicle group chip
    if (this.useVehicleFilter) {
      const chipIndex = this.filterChips.findIndex(chip => chip.type === ChipType.VEHICLE_FILTER);
      const filterName = 'Vehicle';
      let chip: Chip;
      let itemLabel: string;
      if (filter.elements?.length > 0) {
        const firstGroup = filter.elements[0];
        itemLabel = this.vehicleFilterComponent.getLabel(firstGroup);
        if (filter.elements.length > 1) {
          itemLabel = `${itemLabel}, +${filter.elements.length - 1}`;
        }
      } else {
        itemLabel = 'All';
      }
      chip = {
        type: ChipType.VEHICLE_FILTER,
        filterName,
        clear: !filter.elements,
        items: itemLabel,
      };
      if (chipIndex >= 0) {
        this.filterChips[chipIndex] = chip;
      } else {
        this.filterChips.push(chip);
      }
    }

    // emit event to parent
    this.vehicleFilterChanged.emit(new MultiSelectFilter<number>(filter.elements ? filter.elements.map(group => group.id) : null));
    this.appliedFiltersChanged.emit(this.filterChips.filter(chip => !chip.clear).length);
  }

  onDriverChanged(filter: MultiSelectFilter<DriverWithShiftCount>) {
    this.driverFilter = filter.elements;

    // update driver group chip
    if (this.useDriverFilter) {
      const chipIndex = this.filterChips.findIndex(chip => chip.type === ChipType.DRIVER_FILTER);
      const filterName = 'Driver';
      let chip: Chip;
      let itemLabel: string;
      if (filter.elements?.length > 0) {
        const firstGroup = filter.elements[0];
        itemLabel = this.driverFilterComponent.getLabel(firstGroup);
        if (filter.elements.length > 1) {
          itemLabel = `${itemLabel}, +${filter.elements.length - 1}`;
        }
      } else {
        itemLabel = 'All';
      }
      chip = {
        type: ChipType.DRIVER_FILTER,
        filterName,
        clear: !filter.elements,
        items: itemLabel,
      };
      if (chipIndex >= 0) {
        this.filterChips[chipIndex] = chip;
      } else {
        this.filterChips.push(chip);
      }
    }

    // emit event to parent
    this.driverFilterChanged.emit(new MultiSelectFilter<number>(filter.elements ? filter.elements.map(group => group.id) : null));
    this.appliedFiltersChanged.emit(this.filterChips.filter(chip => !chip.clear).length);
  }

  onUserChanged(filter: MultiSelectFilter<UserEmailAsId>) {
    this.userFilter = filter.elements;

    // update user group chip
    if (this.useUserFilter) {
      const chipIndex = this.filterChips.findIndex(chip => chip.type === ChipType.USER_FILTER);
      const filterName = 'Author';
      let chip: Chip;
      let itemLabel: string;
      if (filter.elements?.length > 0) {
        const firstGroup = filter.elements[0];
        itemLabel = this.userFilterComponent.getLabel(firstGroup);
        if (filter.elements.length > 1) {
          itemLabel = `${itemLabel}, +${filter.elements.length - 1}`;
        }
      } else {
        itemLabel = 'All';
      }
      chip = {
        type: ChipType.USER_FILTER,
        filterName,
        clear: !filter.elements,
        items: itemLabel,
      };
      if (chipIndex >= 0) {
        this.filterChips[chipIndex] = chip;
      } else {
        this.filterChips.push(chip);
      }
    }

    // emit event to parent
    this.userFilterChanged.emit(new MultiSelectFilter<string>(filter.elements ? filter.elements.map(group => group.id) : null));
    this.appliedFiltersChanged.emit(this.filterChips.filter(chip => !chip.clear).length);
  }

  onObservationTypeGroupFilterChanged(filter: MultiSelectFilter<ObservationTypeGroup>) {
    this.observationTypeGroupFilter = filter.elements;

    // update vehicle group chip
    if (this.useObservationTypeGroupFilter) {
      const chipIndex = this.filterChips.findIndex(chip => chip.type === ChipType.OBSERVATION_TYPE_GROUP_FILTER);
      const filterName = 'Observation Group';
      let chip: Chip;
      let itemLabel: string;
      if (filter.elements?.length > 0) {
        const firstGroup = filter.elements[0];
        itemLabel = this.otgFilterComponent.getLabel(firstGroup);
        if (filter.elements.length > 1) {
          itemLabel = `${itemLabel}, +${filter.elements.length - 1}`;
        }
      } else {
        itemLabel = 'All';
      }
      chip = {
        type: ChipType.OBSERVATION_TYPE_GROUP_FILTER,
        filterName,
        clear: !filter.elements,
        items: itemLabel,
      };
      if (chipIndex >= 0) {
        this.filterChips[chipIndex] = chip;
      } else {
        this.filterChips.push(chip);
      }
    }

    // emit event to parent
    this.observationTypeGroupFilterChanged.emit(
      new MultiSelectFilter<number>(filter.elements ? filter.elements.map(group => group.id) : null)
    );
    this.appliedFiltersChanged.emit(this.filterChips.filter(chip => !chip.clear).length);
  }

  onAssetStatusFilterChanged(filter: MultiSelectFilter<AssetStatus>) {
    this.assetStatusFilter = filter.elements;

    // update asset status chip
    if (this.useAssetStatusFilter) {
      const chipIndex = this.filterChips.findIndex(chip => chip.type === ChipType.ASSET_STATUS_FILTER);
      const filterName = 'Status';
      let chip: Chip;
      let itemLabel: string;
      if (filter.elements?.length > 0) {
        const firstGroupLabel = filter.elements[0].label;
        itemLabel = filter.elements.length > 1 ? `${firstGroupLabel}, +${filter.elements.length - 1}` : firstGroupLabel;
      } else {
        itemLabel = 'All';
      }
      chip = {
        type: ChipType.ASSET_STATUS_FILTER,
        filterName,
        clear: !filter.elements,
        items: itemLabel,
      };
      if (chipIndex >= 0) {
        this.filterChips[chipIndex] = chip;
      } else {
        this.filterChips.push(chip);
      }
    }

    // emit event to parent
    this.assetStatusFilterChanged.emit(new MultiSelectFilter<string>(filter.elements ? filter.elements.map(status => status.id) : null));
    this.appliedFiltersChanged.emit(this.filterChips.filter(chip => !chip.clear).length);
  }

  onRouteAssignmentStatusFilterChanged(filter: MultiSelectFilter<RouteAssignmentStatusItem>) {
    this.routeAssignmentStatusFilter = filter.elements;
    // update filtering chip
    const chipIndex = this.filterChips.findIndex(chip => chip.type === ChipType.ROUTE_ASSIGNMENT_STATUS_FILTER);
    if (this.useRouteAssignmentStatusFilter) {
      let itemLabel: string;
      if (filter.elements?.length === 1) {
        itemLabel = filter.elements[0].name;
      } else if (filter.elements?.length > 1) {
        itemLabel = `${filter.elements[0].name}, +${filter.elements?.length - 1}`;
      } else {
        itemLabel = 'All';
      }
      const chip = {
        type: ChipType.ROUTE_ASSIGNMENT_STATUS_FILTER,
        filterName: 'Status',
        clear: true,
        items: itemLabel,
      };
      if (chipIndex >= 0) {
        this.filterChips[chipIndex] = chip;
      } else {
        this.filterChips.push(chip);
      }
    } else if (chipIndex >= 0) {
      this.filterChips.splice(chipIndex, 1);
    }

    // emit event to parent
    this.routeAssignmentStatusFilterChanged.emit(
      new MultiSelectFilter<string>(filter.elements ? filter.elements.map(item => item.id) : null)
    );
    this.appliedFiltersChanged.emit(this.filterChips.filter(chip => !chip.clear).length);
  }

  onPassFailFilterChanged(filter: NamedId) {
    this.passFailFilter = filter;
    if (this.usePassFailFilter) {
      const chipIndex = this.filterChips.findIndex(chip => chip.type === ChipType.PASS_FAIL_FILTER);
      const chip = {
        type: ChipType.PASS_FAIL_FILTER,
        filterName: 'Result',
        clear: !filter,
        items: filter?.name ?? 'All',
      };
      if (chipIndex >= 0) {
        this.filterChips[chipIndex] = chip;
      } else {
        this.filterChips.push(chip);
      }
    }

    this.passFailFilterChanged.emit(this.passFailFilter?.id ?? null);
    this.appliedFiltersChanged.emit(this.filterChips.filter(chip => !chip.clear).length);
  }

  filterChipRemoved(e, chip: Chip) {
    switch (chip.type) {
      case ChipType.DATE_FILTER: {
        this.dateFilterComponent.setFilter(TimeRangeFilter.NONE.key);
        break;
      }
      case ChipType.VEHICLE_GROUP_FILTER: {
        this.vgFilterComponent.setFilter(null);
        break;
      }
      case ChipType.VEHICLE_FILTER: {
        this.vehicleFilterComponent.setFilter(null);
        break;
      }
      case ChipType.DRIVER_FILTER: {
        this.driverFilterComponent.setFilter(null);
        break;
      }
      case ChipType.USER_FILTER: {
        this.userFilterComponent.setFilter(null);
        break;
      }
      case ChipType.OBSERVATION_TYPE_GROUP_FILTER: {
        this.otgFilterComponent.setFilter(null);
        break;
      }
      case ChipType.ASSET_STATUS_FILTER: {
        this.asFilterComponent.setFilter(null);
        break;
      }
      case ChipType.ROUTE_ASSIGNMENT_STATUS_FILTER: {
        this.routeAssignmentStatusFilterComponent.setFilter(null);
        break;
      }
      case ChipType.PASS_FAIL_FILTER: {
        this.passFailFilterComponent.setFilter(null);
        break;
      }
      default: {
        console.warn('Unhandled switch case! ' + chip.type);
      }
    }
  }

  filterChipSelected(e, chip: Chip) {
    let trigger: MatMenuTrigger;
    let component: MultiSelectComponent<any> | any;
    let xOffset = 0;
    switch (chip.type) {
      case ChipType.DATE_FILTER: {
        trigger = this.matTriggerDateFilter;
        component = null;
        xOffset = 480;
        break;
      }
      case ChipType.VEHICLE_GROUP_FILTER: {
        trigger = this.matTriggerVehicleGroup;
        component = this.vgFilterComponent;
        break;
      }
      case ChipType.VEHICLE_FILTER: {
        trigger = this.matTriggerVehicle;
        component = this.vehicleFilterComponent;
        xOffset = 200;
        break;
      }
      case ChipType.DRIVER_FILTER: {
        trigger = this.matTriggerDriver;
        component = this.driverFilterComponent;
        xOffset = 200;
        break;
      }
      case ChipType.USER_FILTER: {
        trigger = this.matTriggerUser;
        component = this.userFilterComponent;
        xOffset = 200;
        break;
      }
      case ChipType.OBSERVATION_TYPE_GROUP_FILTER: {
        trigger = this.matTriggerObservationTypeGroup;
        component = this.otgFilterComponent;
        xOffset = 200;
        break;
      }
      case ChipType.ASSET_STATUS_FILTER: {
        trigger = this.matTriggerAssetStatus;
        component = this.asFilterComponent;
        break;
      }
      case ChipType.ROUTE_ASSIGNMENT_STATUS_FILTER: {
        trigger = this.matTriggerRouteAssignmentStatus;
        component = this.routeAssignmentStatusFilterComponent;
        xOffset = 200;
        break;
      }
      case ChipType.PASS_FAIL_FILTER: {
        trigger = this.matTriggerPassFail;
        component = this.passFailFilterComponent;
        break;
      }
      default: {
        console.warn('Unhandled switch case! ' + chip.type);
      }
    }
    if (!trigger) {
      return;
    }
    // entered delay to wait for menu initialization
    setTimeout(() => {
      trigger.openMenu();
      const panelElement: HTMLElement = document.getElementById(trigger.menu.panelId);
      panelElement.style.position = 'absolute';
      panelElement.style.left = (e.view.innerWidth - e.x < xOffset ? e.view.innerWidth - xOffset : e.x) + 'px';
      panelElement.style.top = e.y + 'px';
      panelElement.style.maxHeight = 50 + 'vh';
    }, 100);
  }
}
